import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ButtonGroup, Choose } from "@activecollab/components";
import moment from "moment";
import PropTypes from "prop-types";
import { isAllDay } from "~/components/Calendar/calendarUtils";

export const ReminderType = ({ parent, sendOn, isRelative, relativeValue, onChange }) => {
  const { t } = useTranslation();

  const selected = useMemo(() => {
    if (parent.class === "Task") {
      if (isRelative) {
        return relativeValue === 0 ? 0 : 1;
      }
      return 2;
    }
    if (parent.class === "CalendarEvent") {
      if (isAllDay(parent)) {
        if (isRelative) {
          return relativeValue === 0 ? 0 : 1;
        }
        return 2;
      } else {
        return isRelative ? 0 : 1;
      }
    }
    return null;
  }, [isRelative, parent, relativeValue]);

  const options = useMemo(() => {
    if (parent.class === "Task" && parent.due_on) {
      return [
        {
          name: t("On Due Date"),
          values: {
            is_relative: true,
            relative_value: 0,
            relative_unit: "day",
            is_today_selected: moment().format("YYYY-MM-DD") === moment.unix(parent.due_on).utc().format("YYYY-MM-DD"),
          },
        },
        {
          name: t("Relative to Due Date"),
          values: {
            is_relative: true,
            relative_value: 1,
            relative_unit: "day",
            is_today_selected:
              moment().format("YYYY-MM-DD") === moment.unix(parent.due_on).add(-1, "day").utc().format("YYYY-MM-DD"),
          },
        },
        {
          name: t("Pick Date"),
          values: {
            is_relative: false,
            relative_value: null,
            relative_unit: null,
            is_today_selected: moment().format("YYYY-MM-DD") === moment.unix(sendOn).utc().format("YYYY-MM-DD"),
          },
        },
      ];
    }
    if (parent.class === "CalendarEvent") {
      if (isAllDay(parent)) {
        return [
          {
            name: t("On Event Date"),
            values: {
              is_relative: true,
              relative_value: 0,
              relative_unit: "day",
              is_today_selected:
                moment().format("YYYY-MM-DD") === moment.unix(parent.starts_on).utc().format("YYYY-MM-DD"),
            },
          },
          {
            name: t("Relative to Event Date"),
            values: {
              is_relative: true,
              relative_value: 1,
              relative_unit: "day",
              is_today_selected:
                moment().format("YYYY-MM-DD") ===
                moment.unix(parent.starts_on).add(-1, "day").utc().format("YYYY-MM-DD"),
            },
          },
          {
            name: t("Pick Date"),
            values: {
              is_relative: false,
              relative_value: null,
              relative_unit: null,
              is_today_selected: moment().format("YYYY-MM-DD") === moment.unix(sendOn).utc().format("YYYY-MM-DD"),
            },
          },
        ];
      } else {
        return [
          {
            name: t("Relative to Event Date"),
            values: {
              is_relative: true,
              relative_value: 15,
              relative_unit: "minute",
              is_today_selected:
                moment().format("YYYY-MM-DD") ===
                moment.unix(parent.starts_on).add(-1, "day").utc().format("YYYY-MM-DD"),
            },
          },
          {
            name: t("Pick Date"),
            values: {
              is_relative: false,
              relative_value: null,
              relative_unit: null,
              is_today_selected: moment().format("YYYY-MM-DD") === moment.unix(sendOn).utc().format("YYYY-MM-DD"),
            },
          },
        ];
      }
    }
    return [];
  }, [parent, sendOn, t]);

  const handleChange = useCallback(
    (values) => {
      if (typeof onChange === "function") {
        onChange(values);
      }
    },
    [onChange]
  );

  return (
    <ButtonGroup>
      {options.map((option, index) => (
        <Choose
          key={option.name}
          type="button"
          className="tw-p-2 button-group-choose tw-h-[42px] md:tw-h-[32px]"
          active={selected === index}
          onClick={() => {
            if (selected !== index) {
              handleChange(option.values);
            }
          }}
        >
          {option.name}
        </Choose>
      ))}
    </ButtonGroup>
  );
};

ReminderType.propTypes = {
  parent: PropTypes.object,
  sendOn: PropTypes.number,
  isRelative: PropTypes.bool,
  relativeValue: PropTypes.number,
  onChange: PropTypes.func,
};
